import React from 'react';
import Page from '../../components/Page';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Done from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import { useStaff } from '../../pages/Staff';
import { useManager } from '../../pages/Manager';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Divider, Tooltip, IconButton, Stack,
  Button
} from '@mui/material';
import DocumentForm from './personal-documents/DocumentForm';
import toast from 'react-hot-toast';
import PDFPreviewModal from '../employee/applications/applicationForm/tabs/documents/PDFPreviewModal';
import { useParameterizedQuery } from 'react-fetching-library';
import { downloadCheckboxSectionFileAction, getEmployeeDocumentsAction } from '../../services/request';
import PreviewQuestionnaire from './personal-documents/PreviewQuestionnaire';
import CheckboxSectionForm from '../employee/applications/applicationForm/tabs/documents/additional-documents/CheckboxSectionForm';

export default function StaffPersonalDocuments({ manager }) {
  const { application, setApplication, uploadCheckboxSectionFile, choices } = manager ? useManager() : useStaff();
  const [openAddDocument, setOpenAddDocument] = React.useState(false);
  const [selectedCheckboxSectionId, setSelectedCheckboxSectionId] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [openQuestionnaire, setOpenQuestionnaire] = React.useState(false);
  const { query } = useParameterizedQuery(downloadCheckboxSectionFileAction);
  const { query: agreementQuery } = useParameterizedQuery(getEmployeeDocumentsAction);
  const [openAddCheckboxSection, setOpenAddCheckboxSection] = React.useState(false);
  const [usedCategories, setUsedCategories] = React.useState([]);

  const handleUpload = checkboxSectionId => {
    setSelectedCheckboxSectionId(checkboxSectionId);
    setOpenAddDocument(true);
  };

  const handleDownload = async (row, preview = false) => {
    const { payload, error, loading } = await query(row.id);

    if (!error) {
      if (preview) {
        setPreviewUrl(window.URL.createObjectURL(payload));
      } else {
        const url = window.URL.createObjectURL(payload);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        a.download = `${row.file_name}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } else {
      toast.error('Nie udało się pobrać pliku');
    }
  };

  const handleDownloadAgreement = async (preview = false, templateName, fileName, annexNr = 0) => {
    const { payload, error, loading } = await agreementQuery(application.id, templateName, fileName, annexNr);

    if (!error && !loading) {
      if (preview) {
        setPreviewUrl(window.URL.createObjectURL(payload));
      } else {
        const url = window.URL.createObjectURL(payload);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        a.download = `${fileName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const handleDownloadQuestionnaire = () => {
    setOpenQuestionnaire(true);
  };

  const handleAddCheckboxSection = newSection => {
    setApplication(prev => ({ ...prev, checkbox_sections: [...prev.checkbox_sections, newSection] }));
  };

  React.useEffect(() => {
    if (application?.checkbox_sections) {
      setUsedCategories(application.checkbox_sections.filter(cs => cs.category != 6).map(cs => cs.category));
    }
  }, [application?.checkbox_sections]);

  const StatusIcon = ({ icon, color, text, tooltip }) => (
    <Tooltip title={tooltip}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {React.cloneElement(icon, { fontSize: 'small', color })}
        <Typography variant="caption" color={color}>{text}</Typography>
      </Stack>
    </Tooltip>
  );

  const renderStatus = (section, canAddSection = null) => {
    const noReturn = section.items.find(i => i.name === 'zgadzamy się na niezwracanie');
    if (section.name.includes('UMOWA')) {
      if (application.state == 'AOS') {
        return <StatusIcon icon={<Done />} color="primary" text="Podpisano umowę" tooltip="Umowa została podpisana." />;
      }

      return application.agreement_is_signed
        ? <StatusIcon icon={<Done />} color="primary" text="Podpisano umowę" tooltip="Umowa została podpisana." />
        : <StatusIcon icon={<ClearIcon />} color="error" text="Umowa niepodpisana" tooltip="Umowa nie została podpisana." />;
    }
    if (section.name.includes('KWESTIONARIUSZ')) {
      const stateMap = {
        not_sent: { text: 'Nie wysłany', tooltip: 'Kwestionariusz nie został jeszcze wysłany.' },
        sent: { text: 'Kwestionariusz wysłany', tooltip: 'Kwestionariusz został wysłany. Uzupełnij go.' },
        filled: { text: 'Wypełniony', color: 'primary', tooltip: 'Kwestionariusz został wypełniony.' }
      };
      const state = stateMap[application.questionnaire_state];

      return state ? <StatusIcon icon={<Done />} color={state.color || 'info'} text={state.text} tooltip={state.tooltip} /> : null;
    }
    if (section.name.includes('ANEKS')) {
      const match = section.identifier.match(/^aneks(?<nr>\d+)-/);
      const annexNr = match.groups.nr;

      return application.application_annexes[annexNr - 1].is_signed
        ? <StatusIcon icon={<Done />} color="primary" text="Podpisano aneks" tooltip="Aneks została podpisany." />
        : <StatusIcon icon={<ClearIcon />} color="error" text="Aneks niepodpisany" tooltip="Aneks nie został podpisany." />;
    }
    if (section.name.includes('OŚWIADCZENIE STUDENTA')) {
      return <StatusIcon icon={<Done />} color="primary" text="Dokument zatwierdzony" tooltip="Ten dokument został zatwierdzony wraz z podpisaniem umowy." />;
    }
    if (noReturn?.value) {
      return <StatusIcon icon={<InfoIcon />} text="Nie do zwrotu" tooltip="Tego dokumentu nie musisz zwracać." />;
    }
    if (section.file) {
      const approved = section.items.find(i => i.name === 'zatwierdzone');
      const rejected = section.items.find(i => i.name === 'odrzucone');
      if (canAddSection === false && !approved?.value && !rejected?.value) {
        return <StatusIcon icon={<InfoIcon />} color="info" text="Dokument oczekuje na weryfikację" tooltip="Dokument oczekuje na weryfikację." />;
      }
      if (approved?.value) {
        return <StatusIcon icon={<Done />} color="primary" text="Dokument zatwierdzony" tooltip="Dokument został zatwierdzony." />;
      }
      if (rejected?.value) {
        return <StatusIcon icon={<ClearIcon />} color="error" text="Dokument odrzucony" tooltip="Dokument został odrzucony. Prześlij nowy plik." />;
      }

      return <StatusIcon icon={<InfoIcon />} color="purple" text="Dokument wysłany" tooltip="Dokument został wysłany do biura, gdzie zostanie zatwierdzony, bądź odrzucony. Oczekuj zmiany statusu." />;
    }

    return <StatusIcon icon={<InfoIcon />} color="info" text="Oczekuje na przesłanie" tooltip="Należy wypełnić dokument i przesłać jego skan." />;
  };

  const renderActions = section => {
    const noReturn = section.items.find(i => i.name === 'zgadzamy się na niezwracanie');
    if (section.name.includes('UMOWA')) {
      if (application.state == 'AOS') {
        return null;
      }

      return (
        <Stack direction="row">
          <ActionButton title="Pokaż Umowę" icon={<RemoveRedEyeIcon color="primary" />} onClick={() => handleDownloadAgreement(true, 'agreement', `Umowa nr ${application.agreement_number}`)} />
          <ActionButton title="Pobierz Umowę" icon={<FileDownloadIcon color="info" />} onClick={() => handleDownloadAgreement(false, 'agreement', `Umowa nr ${application.agreement_number}`)} />
        </Stack>
      );
    }
    if (section.name.includes('KWESTIONARIUSZ')) {
      if (application.questionnaire_state === 'filled') {
        return <ActionButton title="Pokaż Kwestionariusz" icon={<RemoveRedEyeIcon color="primary" />} onClick={() => handleDownloadQuestionnaire(section, true)} />;
      }

      return null;

    }

    if (section.name.includes('ANEKS')) {
      const match = section.identifier.match(/^aneks(?<nr>\d+)-/);
      const annexNr = match.groups.nr;

      return (
        <Stack direction="row">
          <ActionButton title="Pokaż Aneks" icon={<RemoveRedEyeIcon color="primary" />} onClick={() => handleDownloadAgreement(true, 'annex', `Aneks nr ${annexNr}`, annexNr)} />
          <ActionButton title="Pobierz Aneks" icon={<FileDownloadIcon color="info" />} onClick={() => handleDownloadAgreement(false, 'annex', `Aneks nr ${annexNr}`, annexNr)} />
        </Stack>
      );
    }

    if (section.name.includes('OŚWIADCZENIE STUDENTA')) {
      return null;
    }

    if (noReturn?.value) {
      return null;
    }
    if (!section.file) {
      return <ActionButton title="Wgraj Plik..." icon={<UploadFileIcon color="primary" />} onClick={() => handleUpload(section.id)} />;
    }
    const rejected = section.items.find(i => i.name === 'odrzucone');

    return (
      <Stack direction="row">
        {section.can_preview && <ActionButton title="Pokaż Plik" icon={<RemoveRedEyeIcon color="primary" />} onClick={() => handleDownload(section, true)} />}
        <ActionButton title="Pobierz Plik" icon={<FileDownloadIcon color="info" />} onClick={() => handleDownload(section)} />
        {rejected?.value && <ActionButton title="Wgraj Plik..." icon={<UploadFileIcon color="primary" />} onClick={() => handleUpload(section.id)} />}
      </Stack>
    );
  };

  const ActionButton = ({ title, icon, onClick }) => (
    <Tooltip title={title}>
      <IconButton onClick={onClick}>{icon}</IconButton>
    </Tooltip>
  );

  const DocumentRow = ({ section, typeSuffix, canAddSection }) => (
    <TableRow>
      <TableCell><Typography variant="caption">{typeSuffix
        ? <Tooltip title={section.file_description}><Typography variant="caption">{section.document_name}</Typography></Tooltip>
        : section.name}</Typography></TableCell>
      {typeSuffix && <TableCell><Typography variant="caption">{section.category_display}</Typography></TableCell>}
      <TableCell>{renderStatus(section, canAddSection)}</TableCell>
      <TableCell>{renderActions(section)}</TableCell>
    </TableRow>
  );

  const DocumentTable = ({ sections, type, typeSuffix = '', canAddSection = null }) => (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Stack direction="row" mb={1}>
        <Typography color="green" sx={{ p: 2 }}>{type} dokumenty {typeSuffix}</Typography>
        {canAddSection &&
          <Button variant="contained" size="small" onClick={() => setOpenAddCheckboxSection(true)}>
              Dodaj Dokument
          </Button>
        }
      </Stack>
      <Divider flexItem />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><b>Nazwa dokumentu</b></TableCell>
            {typeSuffix && <TableCell><b>Kategoria</b></TableCell>}
            <TableCell><b>Status</b></TableCell>
            <TableCell><b>Akcje</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sections
            .filter(section => application.season_year >= 2025 ||
              section.name.includes('UMOWA') ||
              section.name.includes('ANEKS') ||
              section.name.includes('KWESTIONARIUSZ'))
            .map((section, key) => <DocumentRow key={key} section={section} typeSuffix={typeSuffix} canAddSection={canAddSection} />)}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!application) {
    return null;
  }

  return (
    <Page title="Dokumenty Personalne">
      <DocumentTable sections={application.checkbox_sections.filter(s => s.section_type === 'podstawowe')} type="Podstawowe" />
      <DocumentTable sections={application.checkbox_sections.filter(s => s.section_type === 'dodatkowe')} type="Dodatkowe" />
      <DocumentTable
        sections={application.checkbox_sections.filter(s => s.section_type === 'dodatkowe dokumenty personalne dodane przez pracownika')}
        type="Dodatkowe"
        typeSuffix="personalne dodane przez pracownika"
        canAddSection={true}
      />
      <DocumentTable
        sections={application.checkbox_sections.filter(s => s.section_type === 'dodatkowe dokumenty personalne dodane przez biuro')}
        type="Dodatkowe"
        typeSuffix="personalne dodane przez biuro"
        canAddSection={false}
      />

      {openAddDocument && <DocumentForm
        open={openAddDocument}
        handleClose={() => setOpenAddDocument(false)}
        checkboxSectionId={selectedCheckboxSectionId}
        uploadCheckboxSectionFile={uploadCheckboxSectionFile}
      />}
      {previewUrl !== null ? <PDFPreviewModal url={previewUrl} handleClose={() => setPreviewUrl(null)}/> : null}
      {openQuestionnaire && <PreviewQuestionnaire handleClose={() => setOpenQuestionnaire(false)}/> }
      {openAddCheckboxSection && <CheckboxSectionForm
        open={openAddCheckboxSection}
        handleClose={() => setOpenAddCheckboxSection(false)}
        handleAddCheckboxSection={handleAddCheckboxSection}
        rowData={application}
        categories={choices.checkbox_section_categories.filter(csc => !usedCategories.includes(csc.value))}
        officeView={false}
      />}
    </Page>
  );
}
